import type * as quicknew from 'app/screens/quick-new/navigator';
import type * as onBoardingQuicknew from 'app/screens/onboarding/navigator';
import type { PopularItem } from 'app/types/category';
import { Platform } from 'react-native';
import { useNavigation, useRoute } from './navigation';
import { useUISize } from 'app/utils/web-helpers';

export const QUICK_NEW_ITEMS_KEY = 'quick-new-selected-items';

/** Route names for the quick-offers flow (onboarding web vs native / quick-new stack). */
type QuickOfferRouteName =
  | keyof Pick<quicknew.Parameters, 'quick-new-choose' | 'quick-new-confirm' | 'quick-new-thanks'>
  | keyof Pick<onBoardingQuicknew.Parameters, 'choose' | 'confirm' | 'thanks'>;

type GoNextParams = { selectedItems: PopularItem[] } | { userNowContributor: boolean; listingIds?: string[] };

function isWebOnboardingQuickOffersRoute(name: string): name is 'choose' | 'confirm' | 'thanks' {
  return name === 'choose' || name === 'confirm' || name === 'thanks';
}

export function useQuickOffers() {
  const route = useRoute();
  const navigation = useNavigation<QuickOfferRouteName>();
  const { isSmallUI } = useUISize();

  const steps = (() => {
    if (!isSmallUI && isWebOnboardingQuickOffersRoute(route.name)) {
      return [{ name: 'choose' }, { name: 'confirm' }, { name: 'thanks', mode: 'replace' }];
    }
    return [{ name: 'quick-new-choose' }, { name: 'quick-new-confirm' }, { name: 'quick-new-thanks', mode: 'replace' }];
  })();

  const currentIndex = steps.findIndex((step) => route.name === step.name);

  const goToNext = (params?: GoNextParams) => {
    if (currentIndex >= steps.length - 1) return;

    const nextRoute = steps[currentIndex + 1]!;

    if (params && 'selectedItems' in params && Platform.OS === 'web') {
      try {
        sessionStorage.setItem(QUICK_NEW_ITEMS_KEY, JSON.stringify(params.selectedItems));
      } catch {}
    }

    if (nextRoute.mode === 'replace') {
      navigation.replace(
        nextRoute.name as 'thanks' | 'quick-new-thanks',
        params as { userNowContributor: boolean; listingIds?: string[] },
      );
    } else {
      navigation.navigate(
        nextRoute.name as 'confirm' | 'quick-new-confirm',
        params as { selectedItems: PopularItem[] },
      );
    }
  };

  const goToPrevious = () => {
    if (currentIndex <= 0) return;

    const previousRoute = steps[currentIndex - 1]!;
    navigation.navigate(previousRoute.name as 'choose' | 'quick-new-choose');
  };

  return { current: currentIndex, goToNext, goToPrevious, max: steps.length };
}
